import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Unlock, Loader2, ShieldCheck, History, CheckCircle2, XCircle, Paperclip, FileDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ReceiptsDialog } from "@/components/ReceiptsDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `${parseFloat(n || 0) < 0 ? "-" : ""}$${Math.abs(parseFloat(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

const StatusCell = ({ row }) => {
  const lk = row.lock || row.company_lock;
  if (lk) return (
    <div className="flex items-center gap-1.5" data-testid={`signoff-status-${row.month}`}>
      <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"><Lock className="w-3 h-3" /> Locked{!row.lock && " (company)"}</span>
      <span className="text-[11px] text-stone-500 truncate">by {lk.locked_by} · {new Date(lk.locked_at).toLocaleDateString()}{lk.note && ` · “${lk.note}”`}</span>
      {lk.override && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full" data-testid={`signoff-override-${row.month}`}>Override</span>}
    </div>
  );
  return (
    <div className="flex items-center gap-1.5" data-testid={`signoff-status-${row.month}`}>
      <span className="text-[11px] font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">Open</span>
      {row.store_locks?.length > 0 && <span className="text-[11px] text-stone-500">{row.store_locks.length} store lock{row.store_locks.length > 1 ? "s" : ""}: {row.store_locks.map(l => l.store_name).join(", ")}</span>}
    </div>
  );
};

const CheckItem = ({ it, children }) => (
  <div className={`rounded-lg border p-3 ${it.passed ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`} data-testid={`checklist-item-${it.key}`}>
    <div className="flex items-start gap-2">
      {it.passed ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-stone-900">{it.label}</p>
        <p className="text-xs text-stone-600 mt-0.5" data-testid={`checklist-detail-${it.key}`}>{it.detail}</p>
        {children}
      </div>
    </div>
  </div>
);

const ChecklistDialog = ({ target, scopeName, onClose, onDone, onNavigate }) => {
  const [note, setNote] = useState("");
  const [min, setMin] = useState("75");
  const [check, setCheck] = useState(null);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receiptTxn, setReceiptTxn] = useState(null);

  const load = () => {
    if (!target) return;
    axios.get(`${API}/month-locks/checklist`, { params: { month: target.month, store_id: target.store_id, receipt_min: parseFloat(min) || 0 } })
      .then(r => setCheck(r.data)).catch(() => toast.error("Could not load checklist"));
  };
  useEffect(() => { setNote(""); setOverride(false); setCheck(null); setMin("75"); }, [target]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [target, min]);

  const lock = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/month-locks`, { month: target.month, store_id: target.store_id, note, receipt_min: parseFloat(min) || 0, override });
      toast.success(`${monthLabel(target.month)} signed off`); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not lock month"); }
    finally { setBusy(false); }
  };
  const go = (tab) => { onClose(); onNavigate(tab); };
  const items = check?.items || [];
  const it = (k) => items.find(i => i.key === k);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="signoff-lock-dialog">
        <DialogHeader>
          <DialogTitle>Month-end checklist · {target && monthLabel(target.month)}</DialogTitle>
          <DialogDescription>Scope: {scopeName}. Once locked, transactions in this month can't be added, edited or deleted until an owner unlocks it.</DialogDescription>
        </DialogHeader>
        {check === null ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></div> : (
          <div className="space-y-2.5">
            <CheckItem it={it("reconciled")}>
              {!it("reconciled").passed && <button className="text-xs font-medium text-stone-900 underline underline-offset-2 mt-1.5" onClick={() => go("reconcile")} data-testid="checklist-go-reconcile">Go to Reconcile</button>}
            </CheckItem>
            <CheckItem it={it("findings")}>
              {!it("findings").passed && <button className="text-xs font-medium text-stone-900 underline underline-offset-2 mt-1.5" onClick={() => go("ai-analysis")} data-testid="checklist-go-analysis">Review in AI Analysis</button>}
            </CheckItem>
            <CheckItem it={it("receipts")}>
              <div className="flex items-center gap-2 mt-2">
                <Label className="text-xs text-stone-600">Require receipts for expenses of $</Label>
                <Input type="number" min="0" step="1" value={min} onChange={e => setMin(e.target.value)} className="h-7 w-24 text-xs" data-testid="checklist-receipt-min" />
                <span className="text-xs text-stone-500">or more</span>
              </div>
              {check.missing_receipts.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto divide-y divide-amber-100 border border-amber-100 rounded-md bg-white" data-testid="checklist-missing-receipts">
                  {check.missing_receipts.map(t => (
                    <div key={t.id} className="flex items-center gap-3 px-2.5 py-1.5 text-xs" data-testid={`missing-receipt-${t.id}`}>
                      <span className="text-stone-500 w-20">{t.date}</span>
                      <span className="flex-1 truncate text-stone-800">{t.vendor || t.description || t.category}</span>
                      <span className="font-medium">{fmt(t.amount)}</span>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setReceiptTxn(t)} data-testid={`attach-receipt-btn-${t.id}`}><Paperclip className="w-3 h-3 mr-1" /> Attach</Button>
                    </div>
                  ))}
                </div>
              )}
            </CheckItem>
            {!check.all_passed && (
              <label className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50/60 p-3 cursor-pointer" data-testid="checklist-override-label">
                <Checkbox checked={override} onCheckedChange={v => setOverride(!!v)} className="mt-0.5" data-testid="checklist-override-checkbox" />
                <span className="text-xs text-red-800"><span className="font-semibold">Override:</span> lock this month even though not every check passed. The override and the failed checks are printed on the sign-off report.</span>
              </label>
            )}
            <div><Label className="text-xs text-stone-600">Note (optional)</Label>
              <Textarea className="mt-1 text-sm" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Reconciled against Chase statement" data-testid="signoff-note-input" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="signoff-lock-cancel">Cancel</Button>
          <Button className="bg-stone-900 text-white hover:bg-stone-800" disabled={busy || check === null || (!check.all_passed && !override)} onClick={lock} data-testid="signoff-lock-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />} {check && !check.all_passed ? "Lock with override" : "Lock month"}
          </Button>
        </DialogFooter>
        <ReceiptsDialog txn={receiptTxn} onClose={() => setReceiptTxn(null)} onChanged={load} />
      </DialogContent>
    </Dialog>
  );
};

const downloadReport = async (lk) => {
  try {
    const res = await axios.get(`${API}/month-locks/${lk.id}/report`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `signoff-${lk.month}-${(lk.store_name || "all").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) { toast.error("Could not generate report"); }
};

const EventsList = ({ events }) => (
  <Card className="border-stone-200/60" data-testid="signoff-events">
    <CardContent className="p-0">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100"><History className="w-4 h-4 text-stone-500" /><h3 className="text-sm font-semibold text-stone-900">Sign-off log</h3></div>
      {events.length === 0 ? <p className="text-sm text-stone-400 text-center py-6">No sign-offs yet</p> : events.map((e, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-2 text-xs border-t border-stone-50 first:border-t-0" data-testid={`signoff-event-${i}`}>
          {e.action === "locked" ? <Lock className="w-3.5 h-3.5 text-emerald-600" /> : <Unlock className="w-3.5 h-3.5 text-amber-600" />}
          <span className="font-medium text-stone-800">{e.action === "locked" ? "Locked" : "Unlocked"} {monthLabel(e.month)}</span>
          {e.override && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">Override</span>}
          <span className="text-stone-500">{e.store_name}</span>
          {e.note && <span className="text-stone-400 truncate">“{e.note}”</span>}
          <span className="ml-auto text-stone-400">{e.user} · {new Date(e.at).toLocaleString()}</span>
        </div>
      ))}
    </CardContent>
  </Card>
);

export const MonthSignOff = ({ stores, onChanged, onNavigate }) => {
  const { isOwner } = useAuth();
  const [scope, setScope] = useState("all");
  const [rows, setRows] = useState(null);
  const [events, setEvents] = useState([]);
  const [lockTarget, setLockTarget] = useState(null);
  const scopeName = scope === "all" ? "Whole company" : scope === "general" ? "General (No Store)" : stores.find(s => s.id === scope)?.name || scope;

  const load = () => {
    axios.get(`${API}/month-locks/months`, { params: { store_id: scope } }).then(r => setRows(r.data)).catch(() => setRows([]));
    axios.get(`${API}/month-locks`).then(r => setEvents(r.data.events)).catch(() => {});
  };
  useEffect(() => { setRows(null); load(); }, [scope]);
  const refresh = () => { load(); onChanged(); };

  const unlock = async (lk) => {
    try { await axios.delete(`${API}/month-locks/${lk.id}`); toast.success(`${monthLabel(lk.month)} unlocked`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not unlock"); }
  };

  return (
    <div className="space-y-6" data-testid="signoff-tab">
      <Card className="border-stone-200/60">
        <CardContent className="p-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-base font-semibold text-stone-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-stone-500" /> Month Sign-Off</h3>
            <p className="text-sm text-stone-500 mt-1">Lock a reconciled month so its transactions can't be changed by accident. {!isOwner && <span className="text-amber-700">Only owners can lock or unlock.</span>}</p>
          </div>
          <div><Label className="text-xs text-stone-600">Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="mt-1 w-56 text-sm" data-testid="signoff-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Whole company</SelectItem>
                <SelectItem value="general">General (No Store)</SelectItem>
                {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-stone-200/60">
        <CardContent className="p-0">
          {rows === null ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></div>
            : rows.length === 0 ? <p className="text-sm text-stone-400 text-center py-10" data-testid="signoff-empty">No transactions in this scope yet</p> : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs text-stone-500"><tr>
                <th className="px-4 py-2.5 text-left font-medium">Month</th><th className="px-3 py-2.5 text-right font-medium">Txns</th>
                <th className="px-3 py-2.5 text-right font-medium">Money in</th><th className="px-3 py-2.5 text-right font-medium">Money out</th>
                <th className="px-3 py-2.5 text-right font-medium">Net</th><th className="px-4 py-2.5 text-left font-medium">Status</th><th className="px-4 py-2.5" />
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.month} className={`border-t border-stone-100 ${r.lock || r.company_lock ? "bg-emerald-50/30" : ""}`} data-testid={`signoff-row-${r.month}`}>
                    <td className="px-4 py-2.5 font-medium text-stone-900">{monthLabel(r.month)}</td>
                    <td className="px-3 py-2.5 text-right text-stone-600">{r.count}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-700">{fmt(r.income)}</td>
                    <td className="px-3 py-2.5 text-right text-stone-700">{fmt(r.expense)}</td>
                    <td className={`px-3 py-2.5 text-right font-medium ${r.net >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(r.net)}</td>
                    <td className="px-4 py-2.5 max-w-[360px]"><StatusCell row={r} /></td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap space-x-1.5">
                      {(r.lock || r.company_lock) && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadReport(r.lock || r.company_lock)} data-testid={`signoff-pdf-btn-${r.month}`}><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                      )}
                      {isOwner && (r.lock ? (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => unlock(r.lock)} data-testid={`signoff-unlock-btn-${r.month}`}><Unlock className="w-3.5 h-3.5 mr-1" /> Unlock</Button>
                      ) : !r.company_lock && (
                        <Button size="sm" className="h-7 text-xs bg-stone-900 text-white hover:bg-stone-800" onClick={() => setLockTarget({ month: r.month, store_id: scope })} data-testid={`signoff-lock-btn-${r.month}`}><Lock className="w-3.5 h-3.5 mr-1" /> Sign off</Button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <EventsList events={events} />
      <ChecklistDialog target={lockTarget} scopeName={scopeName} onClose={() => setLockTarget(null)} onDone={refresh} onNavigate={onNavigate} />
    </div>
  );
};
